import React from 'react';
import { FileText, Download, CheckCircle, Circle, Clock, Truck, Ship, Anchor, Package } from 'lucide-react';

export const LiveTrackingDashboard = () => {
    const [documents, setDocuments] = React.useState([
        { id: 1, name: "Bill of Lading", type: "PDF", size: "2.4 MB", status: "Verified", date: "Nov 15, 2025" },
        { id: 2, name: "Commercial Invoice", type: "PDF", size: "1.1 MB", status: "Verified", date: "Nov 14, 2025" },
        { id: 3, name: "Packing List", type: "PDF", size: "0.8 MB", status: "Verified", date: "Nov 14, 2025" },
        { id: 4, name: "Customs Declaration (Export)", type: "PDF", size: "1.5 MB", status: "Pending", date: "Nov 16, 2025" },
    ]);

    const [dragOver, setDragOver] = React.useState(false);
    const fileInputRef = React.useRef(null);

    const timelineEvents = [
        { id: 1, title: "Order Placed", date: "Nov 10, 2025", time: "09:00", status: "completed", icon: FileText, location: "Shenzhen, CN" },
        { id: 2, title: "Cargo Ready", date: "Nov 14, 2025", time: "14:30", status: "completed", icon: Package, location: "Shenzhen Warehouse" },
        { id: 3, title: "Gate In", date: "Nov 15, 2025", time: "10:15", status: "completed", icon: Truck, location: "Port of Hong Kong" },
        { id: 4, title: "Loaded on Vessel", date: "Nov 15, 2025", time: "18:45", status: "completed", icon: Anchor, location: "Port of Hong Kong" },
        { id: 5, title: "Departure", date: "Nov 16, 2025", time: "02:00", status: "completed", icon: Ship, location: "Port of Hong Kong" },
        { id: 6, title: "In Transit", date: "Current", time: "Now", status: "current", icon: Ship, location: "Indian Ocean" },
        { id: 7, title: "Arrival at Port", date: "Dec 01, 2025", time: "07:30", status: "upcoming", icon: Anchor, location: "Port of Hamburg" },
        { id: 8, title: "Customs Clearance", date: "Dec 01, 2025", time: "Est. 14:00", status: "upcoming", icon: FileText, location: "Hamburg, DE" },
        { id: 9, title: "Delivered", date: "Dec 02, 2025", time: "Est. 10:00", status: "upcoming", icon: CheckCircle, location: "Berlin Warehouse" },
    ];

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            handleFiles(files);
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            handleFiles(files);
        }
    };

    const handleFiles = (files) => {
        const newDocs = files.map((file, index) => ({
            id: Date.now() + index,
            name: file.name,
            type: file.name.split('.').pop().toUpperCase(),
            size: (file.size / 1024 / 1024).toFixed(1) + " MB",
            status: "Pending Analysis", // Pending AI analysis
            date: new Date().toLocaleDateString("en-US", { month: 'short', day: 'numeric', year: 'numeric' })
        }));
        setDocuments(prev => [...newDocs, ...prev]);

        // Auto-trigger AI for demo purposes if desired, or let user click button
        console.log("Files added:", files);
    };

    const handleAIAnalyze = (doc) => {
        // Mock API call to PIM Agent
        // Identifying designation for prompt engineering
        const payload = {
            designation: "live_tracking_doc_analysis", // Key for AI Settings
            documentId: doc.id,
            filename: doc.name
        };
        console.log("Triggering AI Analysis with payload:", payload);

        // Mock update to status
        setDocuments(prev => prev.map(d =>
            d.id === doc.id ? { ...d, status: "Analyzing..." } : d
        ));

        setTimeout(() => {
            setDocuments(prev => prev.map(d =>
                d.id === doc.id ? { ...d, status: "Verified" } : d
            ));
        }, 2000);
    };

    return (
        <div className="h-full flex gap-6 p-6 overflow-hidden">
            {/* Freight Documents Section */}
            <div className="flex-1 flex flex-col bg-stone-900/50 border border-stone-700 rounded-xl overflow-hidden">
                <div className="bg-stone-950 px-4 py-3 border-b border-stone-800 flex justify-between items-center">
                    <h3 className="text-stone-200 font-bold flex items-center gap-2">
                        <FileText size={18} className="text-blue-400" />
                        Freight Documents
                    </h3>
                </div>

                {/* Compact Drag & Drop Zone */}
                <div
                    className={`mx-4 mt-4 p-4 border-2 border-dashed rounded-lg transition-colors flex flex-col items-center justify-center text-center cursor-pointer
                        ${dragOver ? 'border-blue-500 bg-blue-900/10' : 'border-stone-700 hover:border-stone-500 bg-stone-900/30'}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        multiple
                        onChange={handleFileSelect}
                    />
                    <div className="flex items-center gap-2 text-stone-400 text-xs">
                        <FileText size={14} />
                        <span className="font-medium">Drop files here or click to upload</span>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {documents.map(doc => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-stone-800/40 border border-stone-800 rounded-lg hover:bg-stone-800/60 transition-colors group">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-stone-900 rounded flex items-center justify-center border border-stone-700 text-red-400 font-bold text-xs">
                                    {doc.type}
                                </div>
                                <div>
                                    <div className="text-stone-200 font-medium text-sm">{doc.name}</div>
                                    <div className="text-stone-500 text-xs flex items-center gap-2">
                                        <span>{doc.size}</span>
                                        <span>•</span>
                                        <span>{doc.date}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className={`text-xs px-2 py-0.5 rounded border ${doc.status === 'Verified' ? 'bg-green-900/20 text-green-400 border-green-900/50' :
                                    doc.status === 'Analyzing...' ? 'bg-purple-900/20 text-purple-400 border-purple-900/50 animate-pulse' :
                                        'bg-yellow-900/20 text-yellow-400 border-yellow-900/50'
                                    }`}>
                                    {doc.status}
                                </span>

                                {/* AI Ghost Button - Contextual or for Pending docs */}
                                {doc.status !== 'Verified' && doc.status !== 'Analyzing...' && (
                                    <button
                                        onClick={() => handleAIAnalyze(doc)}
                                        className="p-1.5 text-xs bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded flex items-center gap-1 transition-colors"
                                        title="AI Analyze"
                                    >
                                        <Circle size={10} className="fill-purple-500" /> AI
                                    </button>
                                )}

                                <button className="p-2 text-stone-400 hover:text-white hover:bg-stone-700 rounded transition-colors">
                                    <Download size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Shipment Timeline Section */}
            <div className="flex-1 flex flex-col bg-stone-900/50 border border-stone-700 rounded-xl overflow-hidden">
                <div className="bg-stone-950 px-4 py-3 border-b border-stone-800">
                    <h3 className="text-stone-200 font-bold flex items-center gap-2">
                        <Clock size={18} className="text-blue-400" />
                        Shipment Timeline
                    </h3>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                    <div className="relative pl-4 space-y-6">
                        {/* Vertical Line */}
                        <div className="absolute left-[27px] top-2 bottom-2 w-0.5 bg-stone-800"></div>

                        {timelineEvents.map((event) => {
                            const Icon = event.icon;

                            let statusColor = "text-stone-600 bg-stone-900 border-stone-700"; // Upcoming

                            if (event.status === 'completed') {
                                statusColor = "text-green-400 bg-green-900/20 border-green-900/50";
                            } else if (event.status === 'current') {
                                statusColor = "text-blue-400 bg-blue-900/20 border-blue-500 animate-pulse";
                            }

                            return (
                                <div key={event.id} className="relative flex gap-4 items-start z-10">
                                    <div className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${statusColor}`}>
                                        <Icon size={12} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <div className={`text-sm font-bold ${event.status === 'upcoming' ? 'text-stone-500' : 'text-stone-200'}`}>
                                                    {event.title}
                                                </div>
                                                <div className="text-xs text-stone-500">{event.location}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-xs text-stone-400 font-mono">{event.date}</div>
                                                <div className="text-[10px] text-stone-600">{event.time}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
